import { useEffect, useState } from 'react';

import RestaurantSettingsService from '../../features/restaurant/settings/RestaurantSettingsService';
import { mapAppearanceSettings } from '../../features/restaurant/settings/RestaurantSettingsMapper';
import {
  DEFAULT_RESTAURANT_TABLE_THEME,
  RestaurantTableTheme,
} from '../../types/restaurant/RestaurantTableTypes';

/**
 * Carga la apariencia de las mesas de una sucursal (Configuración › Apariencia) y
 * la aplica como variables CSS (`--rt-*`) en `:root`, para que TODO ícono de mesa
 * —pantalla de Mesas, tablero de Órdenes, modales— use los mismos colores. Al
 * desmontar (o cambiar de sucursal) limpia las variables y vuelve al tema por
 * defecto. Cachea por sucursal para que las revisitas sean instantáneas.
 */
const cache = new Map<number, RestaurantTableTheme>();

const VAR_NAMES: Record<keyof RestaurantTableTheme, string> = {
  tableFillColor: '--rt-table-fill',
  tableLineColor: '--rt-table-line',
  chairFillColor: '--rt-chair-fill',
  chairLineColor: '--rt-chair-line',
};

const applyVars = (theme: RestaurantTableTheme | null) => {
  const root = document.documentElement;
  (Object.keys(VAR_NAMES) as (keyof RestaurantTableTheme)[]).forEach((key) => {
    if (theme) root.style.setProperty(VAR_NAMES[key], theme[key]);
    else root.style.removeProperty(VAR_NAMES[key]);
  });
};

const useRestaurantTableTheme = (branchId: number | '') => {
  const [theme, setTheme] = useState<RestaurantTableTheme>(
    typeof branchId === 'number' && cache.has(branchId)
      ? (cache.get(branchId) as RestaurantTableTheme)
      : DEFAULT_RESTAURANT_TABLE_THEME
  );

  useEffect(() => {
    if (typeof branchId !== 'number') {
      applyVars(null);
      setTheme(DEFAULT_RESTAURANT_TABLE_THEME);
      return;
    }

    let cancelled = false;

    const cached = cache.get(branchId);
    if (cached) {
      applyVars(cached);
      setTheme(cached);
    } else {
      applyVars(DEFAULT_RESTAURANT_TABLE_THEME);
      RestaurantSettingsService.getByBranch(branchId)
        .then((response) => {
          if (cancelled) return;
          const appearance = response.data?.appearance;
          const next: RestaurantTableTheme = appearance
            ? mapAppearanceSettings(appearance)
            : DEFAULT_RESTAURANT_TABLE_THEME;
          cache.set(branchId, next);
          applyVars(next);
          setTheme(next);
        })
        .catch(() => {
          if (cancelled) return;
          applyVars(DEFAULT_RESTAURANT_TABLE_THEME);
          setTheme(DEFAULT_RESTAURANT_TABLE_THEME);
        });
    }

    return () => {
      cancelled = true;
      applyVars(null);
    };
  }, [branchId]);

  return theme;
};

export default useRestaurantTableTheme;
